import { useState, useEffect, useMemo } from "react";
import {
  Upload,
  Calendar,
  Download,
  AlertCircle,
  CheckCircle,
  Search,
  ChevronDown,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { SaveRevenue, RevenueData } from "../../../services/revenueservice";
import { cx, toLocalISOString } from "../../../utils";
import {
  fetchdropdownMall,
  fetchdropdownTenant,
} from "../../../services/dropdown";

interface MallOption {
  value: string;
  label: string;
}
interface TenantOption {
  value: string;
  label: string;
}
interface PreviewRow {
  TransactionNumber: string;
  TransactionDate: string;
  Amount: number;
  Remarks: string;
  UploadStatus: "valid" | "error" | "duplicate";
  ErrorMessage?: string;
}

interface ExcelRow {
  TransactionNumber: string | number;
  TransactionDate: string | number | Date;
  Amount: string | number;
  Remarks?: string | number;
  [key: string]: string | number | Date | undefined;
}

const COLUMN_ALIASES: Record<string, string[]> = {
  TransactionNumber: [
    "transactionnumber",
    "transaction number",
    "transaction_number",
    "no transaksi",
    "notransaksi",
    "no. transaksi",
    "nomor transaksi",
    "no",
    "number",
    "invoice",
    "invoice number",
    "no invoice",
    "noinvoice",
  ],
  TransactionDate: [
    "transactiondate",
    "transaction date",
    "transaction_date",
    "tanggal transaksi",
    "tanggal",
    "tgl transaksi",
    "tgl",
    "date",
    "tanggal invoice",
  ],
  Amount: [
    "amount",
    "nominal",
    "total",
    "jumlah",
    "harga",
    "nilai",
    "total amount",
    "total nominal",
    "total harga",
    "total transaksi",
    "nilai transaksi",
    "grand total",
    "subtotal",
    "sub total",
    "harga total",
  ],
  Remarks: [
    "remarks",
    "keterangan",
    "catatan",
    "description",
    "deskripsi",
    "note",
    "notes",
    "info",
    "informasi",
  ],
};

function normalizeRow(rawRow: Record<string, unknown>): ExcelRow {
  const keyMap: Record<string, string> = {};
  Object.keys(rawRow).forEach((k) => {
    keyMap[k.trim().toLowerCase()] = k;
  });

  const findValue = (field: string): string | number | Date | undefined => {
    const aliases = COLUMN_ALIASES[field] ?? [field.toLowerCase()];
    for (const alias of aliases) {
      const originalKey = keyMap[alias.toLowerCase()];
      if (originalKey !== undefined) {
        const val = rawRow[originalKey];
        if (val !== undefined && val !== null && val !== "") {
          return val as string | number | Date;
        }
      }
    }
    return undefined;
  };

  return {
    TransactionNumber: findValue("TransactionNumber") as string | number ?? "",
    TransactionDate: findValue("TransactionDate") as string | number | Date ?? "",
    Amount: findValue("Amount") as string | number ?? "",
    Remarks: findValue("Remarks") as string | number | undefined,
  };
}

function parseAmount(raw: string | number | undefined): number {
  if (raw === undefined || raw === null || raw === "") return 0;
  if (typeof raw === "number") return raw;

  const str = raw.toString().trim();
  if (!str) return 0;

  const hasDot = str.includes(".");
  const hasComma = str.includes(",");

  let normalized = str;

  if (hasDot && hasComma) {
    const lastDot = str.lastIndexOf(".");
    const lastComma = str.lastIndexOf(",");

    if (lastComma > lastDot) {
      normalized = str.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = str.replace(/,/g, "");
    }
  } else if (hasDot && !hasComma) {
    const dotParts = str.split(".");
    const allPartsAreShort = dotParts.slice(1).every((p) => p.length === 3);
    if (dotParts.length > 1 && allPartsAreShort) {
      normalized = str.replace(/\./g, "");
    }
  } else if (!hasDot && hasComma) {
    const commaParts = str.split(",");
    const allPartsAreShort = commaParts.slice(1).every((p) => p.length === 3);
    if (commaParts.length > 1 && allPartsAreShort) {
      normalized = str.replace(/,/g, "");
    } else {
      normalized = str.replace(",", ".");
    }
  }

  const result = parseFloat(normalized);
  return isNaN(result) ? 0 : result;
}

export default function TemplateUpload() {
  const today = new Date();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(today.getFullYear(), today.getMonth(), 1),
    new Date(today.getFullYear(), today.getMonth() + 1, 0),
  ]);
  const [startDate, endDate] = dateRange;
  const [fileName, setFileName] = useState("");
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [rawRevenueData, setRawRevenueData] = useState<RevenueData[]>([]);

  const [mallOptions, setMallOptions] = useState<MallOption[]>([]);
  const [selectedMall, setSelectedMall] = useState("");
  const [mallSearchQuery, setMallSearchQuery] = useState("");
  const [isMallDropdownOpen, setIsMallDropdownOpen] = useState(false);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [tenantSearchQuery, setTenantSearchQuery] = useState("");
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchMalls = async () => {
      try {
        const response = await fetchdropdownMall(0);
        if (!response?.dropdownClassDatas) return;
        const options: MallOption[] = response.dropdownClassDatas.map(
          (m: { DataId: number; DataLabel: string }) => ({
            value: m.DataId.toString(),
            label: m.DataLabel,
          })
        );
        setMallOptions(options);
        const storedMallId =
          localStorage.getItem("mall_id") ||
          sessionStorage.getItem("mall_id");
        if (storedMallId && options.find((o) => o.value === storedMallId)) {
          setSelectedMall(storedMallId);
        } else if (options.length === 1) {
          setSelectedMall(options[0].value);
          localStorage.setItem("mall_id", options[0].value);
        } else {
          setSelectedMall("");
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch mall options",
        });
      }
    };
    fetchMalls();
  }, []);


  useEffect(() => {
    if (!selectedMall) {
      setTenantOptions([]);
      setSelectedTenant("");
      return;
    }
    const fetchTenants = async () => {
      try {
        const response = await fetchdropdownTenant(parseInt(selectedMall, 10));
        if (!response?.dropdownClassDatas) return;
        const options: TenantOption[] = response.dropdownClassDatas.map(
          (t: { DataId: number; DataLabel: string }) => ({
            value: t.DataId.toString(),
            label: t.DataLabel,
          })
        );
        setTenantOptions(options);
        if (options.length === 1) {
          setSelectedTenant(options[0].value);
          localStorage.setItem("tenant_id", options[0].value);
        } else {
          const storedTenantId =
            localStorage.getItem("tenant_id") ||
            sessionStorage.getItem("tenant_id");
          if (storedTenantId && options.find((o) => o.value === storedTenantId)) {
            setSelectedTenant(storedTenantId);
          } else {
            setSelectedTenant("");
          }
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch tenant options",
        });
      }
    };
    fetchTenants();
  }, [selectedMall]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".mall-dropdown-container")) {
        setIsMallDropdownOpen(false);
        setMallSearchQuery("");
      }
      if (!target.closest(".tenant-dropdown-container")) {
        setIsTenantDropdownOpen(false);
        setTenantSearchQuery("");
      }
    };
    if (isMallDropdownOpen || isTenantDropdownOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMallDropdownOpen, isTenantDropdownOpen]);

  const filteredMallOptions = useMemo(() => {
    if (!mallSearchQuery) return mallOptions;
    return mallOptions.filter((o) =>
      o.label.toLowerCase().includes(mallSearchQuery.toLowerCase())
    );
  }, [mallOptions, mallSearchQuery]);

  const filteredTenantOptions = useMemo(() => {
    if (!tenantSearchQuery) return tenantOptions;
    return tenantOptions.filter((o) =>
      o.label.toLowerCase().includes(tenantSearchQuery.toLowerCase())
    );
  }, [tenantOptions, tenantSearchQuery]);

  const selectedMallLabel =
    mallOptions.find((o) => o.value === selectedMall)?.label || "Select Mall";
  const selectedTenantLabel =
    tenantOptions.find((o) => o.value === selectedTenant)?.label ||
    "Select Store";
  const isMallDisabled = mallOptions.length === 1;
  const isTenantDisabled =
    !selectedMall || tenantOptions.length === 0 || tenantOptions.length === 1;

  const handleMallSelect = (value: string) => {
    setSelectedMall(value);
    localStorage.setItem("mall_id", value);
    setIsMallDropdownOpen(false);
    setMallSearchQuery("");
    setSelectedTenant("");
    resetUpload();
  };

  const handleTenantSelect = (value: string) => {
    setSelectedTenant(value);
    localStorage.setItem("tenant_id", value);
    setIsTenantDropdownOpen(false);
    setTenantSearchQuery("");
    resetUpload();
  };

  const resetUpload = () => {
    setFileName("");
    setPreviewData([]);
    setTotalAmount(0);
    setHasError(false);
    setRawRevenueData([]);
    const input = document.getElementById("fileInput") as HTMLInputElement;
    if (input) input.value = "";
  };

  const parseTransactionDate = (
    raw: string | number | Date | undefined
  ): Date | null => {
    if (!raw && raw !== 0) return null;

    if (typeof raw === "number") {
      const parsed = XLSX.SSF.parse_date_code(raw);
      if (parsed && parsed.y > 1900) {
        return new Date(parsed.y, parsed.m - 1, parsed.d);
      }
      return null;
    }

    if (typeof raw === "string") {
      const str = raw.trim();
      if (!str) return null;

      const dmyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (dmyMatch) {
        return new Date(+dmyMatch[3], +dmyMatch[2] - 1, +dmyMatch[1]);
      }

      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]);
      }

      const direct = new Date(str);
      return isNaN(direct.getTime()) ? null : direct;
    }

    if (raw instanceof Date) {
      if (isNaN(raw.getTime())) return null;
      return new Date(raw.getUTCFullYear(), raw.getUTCMonth(), raw.getUTCDate());
    }

    return null;
  };

  const parseExcel = (file: File, sd: Date | null, ed: Date | null) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, {
        type: "binary",
        cellDates: false,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      // ── PATCH: Batasi range sheet hanya ke 4 kolom pertama (A–D) ──
      // Ini mencegah kolom E+ yang terisi secara tidak sengaja terbaca
      // dan menyebabkan error validasi palsu.
      const ref = sheet["!ref"];
      if (ref) {
        const range = XLSX.utils.decode_range(ref);
        // Paksa kolom maksimum = 3 (kolom D, 0-indexed)
        if (range.e.c > 3) {
          range.e.c = 3;
          sheet["!ref"] = XLSX.utils.encode_range(range);
        }
      }

      // Baca sheet; tanpa defval agar kolom kosong tidak menghasilkan
      // key __EMPTY yang mengganggu proses normalizeRow
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        raw: true,
      });

      // Hapus baris yang seluruh nilainya kosong / undefined
      const nonEmptyRawRows = rawRows.filter((rawRow) =>
        Object.values(rawRow).some(
          (v) => v !== undefined && v !== null && String(v).trim() !== ""
        )
      );

      const rows: ExcelRow[] = nonEmptyRawRows.map(normalizeRow);

      const sdNorm = sd
        ? new Date(sd.getFullYear(), sd.getMonth(), sd.getDate())
        : null;
      const edNorm = ed
        ? new Date(ed.getFullYear(), ed.getMonth(), ed.getDate())
        : null;

      let foundError = false;
      let total = 0;
      const preview: PreviewRow[] = [];
      const validRevenue: RevenueData[] = [];

      rows.forEach((row) => {
        const transactionNumber =
          row["TransactionNumber"]?.toString().trim() ?? "";
        const rawDate = row["TransactionDate"];

        const amount = parseAmount(row["Amount"] as string | number | undefined);

        const remarks =
          row["Remarks"] !== undefined && row["Remarks"] !== ""
            ? row["Remarks"].toString()
            : "";

        // Lewati baris yang benar-benar kosong (setelah normalisasi)
        if (!transactionNumber && !rawDate && amount === 0) return;

        const txDate = parseTransactionDate(
          rawDate as string | number | Date | undefined
        );
        const isValidDate = txDate !== null && !isNaN(txDate.getTime());

        const txDateNorm = isValidDate
          ? new Date(
            txDate!.getFullYear(),
            txDate!.getMonth(),
            txDate!.getDate()
          )
          : null;

        let errorMessage = "";

        if (
          !isValidDate ||
          !txDateNorm ||
          (sdNorm && txDateNorm < sdNorm) ||
          (edNorm && txDateNorm > edNorm)
        ) {
          errorMessage = "Invalid Transaction Date";
        }

        if (errorMessage) {
          foundError = true;
          preview.push({
            TransactionNumber: transactionNumber || "-",
            TransactionDate:
              isValidDate && txDate
                ? txDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : "Invalid Date",
            Amount: amount,
            Remarks: remarks,
            UploadStatus: "error",
            ErrorMessage: errorMessage,
          });
        } else {
          total += amount;
          preview.push({
            TransactionNumber: transactionNumber,
            TransactionDate: txDate!.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
            Amount: amount,
            Remarks: remarks,
            UploadStatus: "valid",
          });
          validRevenue.push({
            RevenueId: 0,
            TransactionNumber: transactionNumber,
            TransactionDate: toLocalISOString(txDate!),
            Amount: amount,
            Remarks: remarks,
          });
        }
      });

      setPreviewData(preview);
      setTotalAmount(total);
      setHasError(foundError);
      setRawRevenueData(validRevenue);
    };
    reader.readAsBinaryString(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      parseExcel(file, startDate, endDate);
    }
  };

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    setDateRange(update);
    const input = document.getElementById("fileInput") as HTMLInputElement;
    if (input?.files?.[0]) parseExcel(input.files[0], update[0], update[1]);
  };

  const handleUpload = async () => {
    if (!selectedMall) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a mall first.",
      });
      return;
    }
    if (!selectedTenant) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a store first.",
      });
      return;
    }
    if (!fileName || previewData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a valid Excel file.",
      });
      return;
    }
    if (hasError) {
      Swal.fire({
        icon: "error",
        title: "Upload Blocked",
        text: "Please fix all errors in the data before uploading.",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await SaveRevenue(
        parseInt(selectedTenant, 10),
        rawRevenueData
      );
      if (!result) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to connect to server.",
        });
        return;
      }
      const duplicateNumbers = new Set<string>();
      if (result.ErrorTransactionNumber?.length > 0) {
        result.ErrorTransactionNumber.forEach((item: string) => {
          const parts = item.split("|");
          if (parts.length === 2 && parts[1].toLowerCase() === "duplicate")
            duplicateNumbers.add(parts[0].trim());
        });
      }
      if (duplicateNumbers.size > 0) {
        setPreviewData((prev) =>
          prev.map((row) =>
            duplicateNumbers.has(row.TransactionNumber)
              ? {
                ...row,
                UploadStatus: "duplicate" as const,
                ErrorMessage: "Duplicate",
              }
              : row
          )
        );
        setHasError(true);
      }
      Swal.fire(
        result.TotalSuccess > 0 ? result.Message : "Upload Data Error",
        `<div><p>Total Success: ${result.TotalSuccess}</p><p>Total Error: ${result.TotalError}</p></div>`,
        result.TotalError === 0 ? "success" : "warning"
      );
      if (result.TotalError === 0) resetUpload();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2 mall-dropdown-container relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-white">
                Mall
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    !isMallDisabled && setIsMallDropdownOpen(!isMallDropdownOpen)
                  }
                  disabled={isMallDisabled}
                  className={cx(
                    "w-full px-4 py-3 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white flex items-center justify-between",
                    isMallDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                  )}
                >
                  <span className={!selectedMall ? "text-gray-400" : ""}>
                    {selectedMallLabel}
                  </span>
                  <ChevronDown
                    className={cx(
                      "w-4 h-4 text-gray-400 transition-transform",
                      isMallDropdownOpen && "rotate-180"
                    )}
                  />
                </button>
                {isMallDropdownOpen && !isMallDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={mallSearchQuery}
                          onChange={(e) => setMallSearchQuery(e.target.value)}
                          placeholder="Search mall..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredMallOptions.length > 0 ? (
                        filteredMallOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleMallSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedMall === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
                          No mall found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 tenant-dropdown-container relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-white">
                Store
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    !isTenantDisabled &&
                    setIsTenantDropdownOpen(!isTenantDropdownOpen)
                  }
                  disabled={isTenantDisabled}
                  className={cx(
                    "w-full px-4 py-3 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white flex items-center justify-between",
                    isTenantDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                  )}
                >
                  <span className={!selectedTenant ? "text-gray-400" : ""}>
                    {selectedTenantLabel}
                  </span>
                  <ChevronDown
                    className={cx(
                      "w-4 h-4 text-gray-400 transition-transform",
                      isTenantDropdownOpen && "rotate-180"
                    )}
                  />
                </button>
                {isTenantDropdownOpen && !isTenantDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          placeholder="Search store..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTenantOptions.length > 0 ? (
                        filteredTenantOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleTenantSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedTenant === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
                          No store found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Calendar className="w-4 h-4 text-indigo-600" /> Date Range
              </label>
              <div className="relative">
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select date range"
                  popperPlacement="bottom-start"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  wrapperClassName="w-full"
                  calendarClassName="shadow-xl border-0"
                />
                <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                  Excel File (.xlsx or .xltx)
                </label>
                <a
                  href="/files/tamplate_revenueupload.xlsx"
                  download
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Download Template
                </a>
              </div>
              <div className="relative">
                <input
                  type="file"
                  accept=".xlsx,.xltx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="fileInput"
                />
                <label
                  htmlFor="fileInput"
                  className="flex items-center justify-center w-full px-4 py-8 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white cursor-pointer group"
                >
                  <div className="text-center">
                    <Upload className="w-12 h-12 text-gray-400 group-hover:text-indigo-500 mx-auto mb-3 transition-colors" />
                    <p className="text-sm font-medium text-gray-600 group-hover:text-indigo-600">
                      {fileName || "Click to choose file or drag and drop"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Excel files only (.xlsx, .xltx)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 sm:gap-4 p-4 sm:p-6">
          <button
            onClick={handleUpload}
            disabled={hasError || isUploading || previewData.length === 0}
            className="flex-1 bg-blue-600 text-white font-semibold py-3 sm:py-4 rounded-xl shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
          <button
            onClick={() => {
              setSelectedTenant("");
              resetUpload();
            }}
            className="px-6 sm:px-8 bg-gray-200 text-gray-700 font-semibold py-3 sm:py-4 rounded-xl hover:bg-gray-300 transition-all"
          >
            Reset
          </button>
        </div>
      </div>

      {previewData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-bold text-gray-800 dark:text-white">
              Data Preview
            </h2>
          </div>
          <div className="p-4 sm:p-5">
            {hasError && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">
                  Some rows have errors. Please fix the Excel file and re-upload.
                </span>
              </div>
            )}

            <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
              <table className="w-full text-sm">
                <thead className="bg-white dark:bg-gray-700 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      #
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      Transaction No.
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      Remarks
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {previewData.map((row, i) => (
                    <tr
                      key={i}
                      className={
                        row.UploadStatus === "error"
                          ? "bg-red-50 dark:bg-red-900/20"
                          : row.UploadStatus === "duplicate"
                            ? "bg-yellow-50 dark:bg-yellow-900/20"
                            : ""
                      }
                    >
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                        {row.TransactionNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                        {row.TransactionDate}
                      </td>
                      <td className="px-4 py-3 text-left text-gray-800 dark:text-gray-200">
                        {row.Amount.toLocaleString("id-ID")}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {row.Remarks}
                      </td>
                      <td className="px-4 py-3">
                        {row.UploadStatus === "valid" ? (
                          <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                            <CheckCircle className="w-4 h-4" /> Valid
                          </span>
                        ) : row.UploadStatus === "duplicate" ? (
                          <span className="flex items-center gap-1 text-yellow-600 text-xs font-medium">
                            <AlertCircle className="w-4 h-4" /> Duplicate
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                            <AlertCircle className="w-4 h-4" /> {row.ErrorMessage}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="block sm:hidden flex flex-col gap-3">
              {previewData.map((row, i) => (
                <div
                  key={i}
                  className={cx(
                    "rounded-xl border p-4 flex flex-col gap-2 shadow-sm",
                    row.UploadStatus === "error"
                      ? "border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800"
                      : row.UploadStatus === "duplicate"
                        ? "border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800"
                        : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                      #{i + 1} {row.TransactionNumber}
                    </span>
                    {row.UploadStatus === "valid" ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle className="w-3.5 h-3.5" /> Valid
                      </span>
                    ) : row.UploadStatus === "duplicate" ? (
                      <span className="flex items-center gap-1 text-yellow-600 text-xs font-medium">
                        <AlertCircle className="w-3.5 h-3.5" /> Duplicate
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                        <AlertCircle className="w-3.5 h-3.5" /> {row.ErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16 shrink-0">Date</span>
                      <span className="text-gray-800 dark:text-gray-100">
                        {row.TransactionDate}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16 shrink-0">Amount</span>
                      <span className="text-gray-800 dark:text-gray-100 font-medium">
                        Rp {row.Amount.toLocaleString("id-ID")}
                      </span>
                    </div>
                    {row.Remarks && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 w-16 shrink-0">Remarks</span>
                        <span className="text-gray-600 dark:text-gray-300 break-all">
                          {row.Remarks}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-1">
              <p className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
                Total Amount : Rp {totalAmount.toLocaleString("id-ID")}
              </p>
              <p className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
                Total Data : {previewData.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}